import { Stack } from '@mui/material';
import { DotLoading } from 'antd-mobile';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useParams } from 'react-router-dom';

const WalletHome = lazy(() => import('@/features/wallet/pages/HomePage'));
const Withdraw = lazy(() => import('@/features/wallet/pages/WithdrawPage'));
const CouponDistribution = lazy(() => import('@/features/shopCoupon/pages/DistributionPage'));
const CouponDetail = lazy(() => import('@/features/shopCoupon/pages/DetailPage'));
const NotFoundPage = lazy(() => import('@/features/notFound/pages/NotFoundPage'));
const Terms = lazy(() => import('@/shared/pages/TermsPage'));
const UserReservationCompletePage = lazy(
  () => import('@/features/userReservation/pages/UserReservationCompletePage'),
);
const UserReservationConfirmPage = lazy(
  () => import('@/features/userReservation/pages/UserReservationConfirmPage'),
);
const UserShareReservationPage = lazy(
  () => import('@/features/userReservation/pages/UserShareReservationPage'),
);
const BrowserOpenGuidePage = lazy(() => import('@/features/browserOpenGuide/pages/WpaGuidePage'));

// ---------- マイクーポン ---------- //
const MyCouponPage = lazy(() => import('@/features/myCoupon/pages/DetailPage'));

// ---------- 新增懒加载组件 ---------- //
const ShareProfileOverlayPage = lazy(() => import('@/features/browserOpenGuide/pages/ShareProfileOverlayPage'));
const ShareOverlayPage = lazy(() => import('@/features/browserOpenGuide/pages/ShareOverlayPage'));
const OpeningHoursPage = lazy(() => import('@/features/shopRegistration/pages/OpeningHoursPage'));
const RegistrationConfirmPage = lazy(
  () => import('@/features/shopRegistration/pages/RegistrationConfirmPage'),
);
const ShopRegistrationStepPage = lazy(
  () => import('@/features/shopRegistration/pages/ShopRegistrationStepPage'),
);
const BankLoginPage = lazy(() => import('@/features/wallet/components/Bank'));
const IncomeTaxDetailPage = lazy(() => import('@/features/wallet/pages/IncomeTaxDetailPage'));
const RewardYearDetailPage = lazy(() => import('@/features/wallet/pages/RewardYearDetailPage'));
const WalletSettingPage = lazy(() => import('@/features/wallet/pages/WalletSettingPage'));
const WalletStepPage = lazy(() => import('@/features/wallet/pages/WalletStepPage'));
const WpaHome = lazy(() => import('@/features/wpa/pages/HomePage'));

function DefaultSkeleton() {
  return (
    <Stack className="flex h-screen w-full flex-col items-center justify-center">
      <DotLoading color="primary" />
    </Stack>
  );
}

function FallbackComponent() {
  return <DefaultSkeleton />;
}

function DynamicDocumentRoute() {
  const { documentName } = useParams<{ documentName: string }>();
  const location = useLocation();

  console.log('DynamicDocumentRoute - pathname:', location.pathname);
  console.log('DynamicDocumentRoute - documentName:', documentName);

  if (!documentName || !documentName.startsWith('terms')) {
    console.log('Not a terms route, redirecting to 404');
    return (
      <Suspense fallback={<DefaultSkeleton />}>
        <NotFoundPage />
      </Suspense>
    );
  }

  // 解析 showHeader：优先使用 location.state，其次查询参数（默认 true）
  const routeState = (location.state as { showHeader?: boolean } | null) || null;
  let showHeader = true;
  if (routeState && typeof routeState.showHeader === 'boolean') {
    showHeader = routeState.showHeader;
  } else {
    // 支持 1/0/true/false
    const searchParams = new URLSearchParams(location.search);
    const showHeaderParam = searchParams.get('showHeader');
    showHeader = showHeaderParam === null ? true : !/^(0|false)$/i.test(String(showHeaderParam));
  }

  console.log('Loading Terms component with name:', documentName, 'showHeader:', showHeader);
  return <Terms key={documentName} name={documentName} showHeader={showHeader} />;
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <Suspense fallback={<FallbackComponent />}>
        <Routes>
          {/* 优惠券 */}
          <Route element={<CouponDistribution />} path="/shopCouponDistribution" />
          <Route element={<CouponDetail />} path="/shopCoupon" />
          <Route element={<MyCouponPage />} path="/myCoupon" />

          {/* 预约 */}
          <Route element={<UserReservationConfirmPage />} path="/userReservationConfirm" />
          <Route element={<UserReservationCompletePage />} path="/userReservationComplete" />
          <Route element={<UserShareReservationPage />} path="/userShareReservation" />

          {/* 钱包 */}
          <Route element={<WalletHome />} path="/walletHome" />
          <Route element={<Withdraw />} path="/withdraw" />
          <Route element={<BankLoginPage />} path="/bankLoginPage" />
          <Route element={<WalletSettingPage />} path="/walletSetting" />
          <Route element={<WalletStepPage />} path="/walletStep" />
          <Route element={<IncomeTaxDetailPage />} path="/incomeTaxDetail" />
          <Route element={<RewardYearDetailPage />} path="/rewardYearDetail" />

          {/* WPA 邀请 */}
          <Route element={<BrowserOpenGuidePage />} path="/openInvitation" />

          {/* WPA 首页 */}
          <Route element={<WpaHome />} path="/wpaHome" />

          {/* 用户主页分享 */}
          <Route element={<ShareProfileOverlayPage />} path="/shareProfile" />

          {/* 用户视频分享 */}
          <Route element={<ShareOverlayPage />} path="/share" />

          {/* 分享覆盖页 */}
          <Route element={<ShareOverlayPage />} path="/shareOverlay" />

          {/* 店舗登録 */}
          <Route element={<ShopRegistrationStepPage />} path="/shopRegistration" />

          {/* 营业时间 */}
          <Route element={<OpeningHoursPage />} path="/openingHours" />

          {/* 店铺确认信息 */}
          <Route element={<RegistrationConfirmPage />} path="/shopConfirmInfo" />

          {/* 其它 */}
          <Route element={<NotFoundPage />} path="/404" />

          {/* 动态文档路由 - 捕获所有的文档路径 */}
          <Route element={<DynamicDocumentRoute />} path="/:documentName" />

          {/* 通配符路由必须放在最后 */}
          <Route element={<NotFoundPage />} path="/*" />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
